// src/features/goal/components/ObjectiveCreateModal.tsx
// Goal 創建 Modal（內部實現為 Objective）- 極簡版本
//
// 術語說明 (2025-11-25)：
// - UI 層：顯示 "Goal"（面向用戶）
// - 代碼層：使用 "Objective"（內部實現）
//
// 重構記錄 (2025-10-28):
// - 極簡化：只需輸入標題
// - 移除 category, priority, description, tags
// - 自動檢查 Active 限制（最多 3 個）
//
// 更新記錄 (2025-10-29):
// - 重新加入 category 選擇器（Apple 風格）
// - Category 可選，預設 'personal'

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import ObjectiveService from '../services/objectiveService';
import { logger } from '../../../utils/logger';
import {
  ObjectiveCategory,
  OBJECTIVE_CATEGORY_CONFIG
} from '../types/objective';
import { DEFAULT_OBJECTIVE_CATEGORY } from '../config/categoryMapping';

interface ObjectiveCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Goal 創建 Modal（內部實現為 Objective）
 *
 * 術語約定：
 * - UI 顯示："New Goal" / "Create Goal"（用戶友好）
 * - 代碼實現：ObjectiveService（技術一致性）
 *
 * 極簡設計：只需輸入標題即可創建
 * 自動處理 Active 數量限制（最多 3 個）
 */
const ObjectiveCreateModal: React.FC<ObjectiveCreateModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [title, setTitle] = useState('');
  // 預設 category，提供良好的用戶體驗（減少操作步驟）
  // 使用統一的配置常量，確保前後端預設值一致
  const [selectedCategory, setSelectedCategory] = useState<ObjectiveCategory>(DEFAULT_OBJECTIVE_CATEGORY);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeCount, setActiveCount] = useState(0);
  const [checkingCount, setCheckingCount] = useState(true);

  // 當 Modal 打開時，檢查 Active 數量並重置狀態
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setError(null);
      loadActiveCount();
    }
  }, [isOpen]);

  // 載入 Active Objectives 數量
  const loadActiveCount = async () => {
    try {
      setCheckingCount(true);
      const count = await ObjectiveService.getActiveObjectivesCount();
      setActiveCount(count);
      logger.dev('ObjectiveCreateModal: Active count loaded', { count });
    } catch (err) {
      logger.error('ObjectiveCreateModal: Failed to get active count', err);
      setActiveCount(0);  // 發生錯誤時假設 0，允許創建
    } finally {
      setCheckingCount(false);
    }
  };

  // 處理表單提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (title.trim().length < 3) {
      setError('Title must be at least 3 characters');
      return;
    }

    if (title.trim().length > 80) {
      setError('Title must be at most 80 characters');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      // 根據 Active 數量決定 status
      const status = activeCount >= 3 ? 'paused' : 'active';

      // 創建（含 category）
      const objectiveId = await ObjectiveService.createObjective({
        title: title.trim(),
        category: selectedCategory,
        status,
      });

      logger.info('ObjectiveCreateModal: Objective created successfully', {
        objectiveId,
        status,
      });

      // 成功：重置並關閉
      setTitle('');
      onSuccess();
      onClose();

    } catch (err) {
      logger.error('ObjectiveCreateModal: Failed to create objective', err);
      setError(err instanceof Error ? err.message : 'Failed to create objective');
    } finally {
      setCreating(false);
    }
  };

  // 處理關閉
  const handleClose = () => {
    if (!creating) {
      setTitle('');
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4
                    bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh]
                      overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">
            New Goal
          </h2>
          <button
            onClick={handleClose}
            disabled={creating}
            aria-label="Close"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* 錯誤提示 */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* 標題輸入 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What do you want to achieve?
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl
                         focus:ring-2 focus:ring-sky-500 focus:border-transparent
                         text-[17px] transition-all
                         disabled:bg-gray-50 disabled:cursor-not-allowed"
              placeholder="e.g., Learn Japanese N2"
              maxLength={80}
              autoFocus
              required
              disabled={creating || checkingCount}
            />
            <div className="mt-2 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {title.length} / 80 characters
              </p>
              {title.trim().length > 0 && title.trim().length < 3 && (
                <p className="text-xs text-red-500">
                  At least 3 characters required
                </p>
              )}
            </div>
          </div>

          {/* Category Picker - Apple 風格 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Category
            </label>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(OBJECTIVE_CATEGORY_CONFIG)
                // 過濾 legacy 分類（不顯示在創建 UI）
                .filter(([_, config]) => !config.isLegacy)
                // 按使用頻率排序
                .sort((a, b) => {
                  const order: ObjectiveCategory[] = [
                    'work', 'health', 'growth', 'finance',
                    'romance', 'social', 'leisure', 'environment'
                  ];
                  return order.indexOf(a[0] as ObjectiveCategory) - order.indexOf(b[0] as ObjectiveCategory);
                })
                .map(([key, config]) => {
                  const Icon = config.icon;
                  const isSelected = selectedCategory === key;

                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedCategory(key as ObjectiveCategory)}
                      disabled={creating || checkingCount}
                      className={`
                        flex flex-col items-center justify-center gap-1.5
                        p-3 rounded-xl border transition-all duration-150
                        disabled:opacity-50 disabled:cursor-not-allowed
                        ${isSelected
                          ? 'bg-sky-50 border-sky-200'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                        }
                      `}
                    >
                      <Icon
                        size={20}
                        className={isSelected ? 'text-sky-600' : 'text-gray-400'}
                        strokeWidth={2}
                      />
                      <span className={`text-xs font-medium ${
                        isSelected ? 'text-sky-600' : 'text-gray-500'
                      }`}>
                        {config.label}
                      </span>
                    </button>
                  );
                })
              }
            </div>
          </div>

          {/* Active 限制提示 - 輕量樣式，移至按鈕上方 */}
          {!checkingCount && activeCount >= 3 && (
            <div className="mb-4 px-4 py-3 bg-yellow-50/50 rounded-lg">
              <p className="text-xs text-yellow-700 text-center">
                You have 3 active objectives. This will be paused.
              </p>
            </div>
          )}

          {/* 提交按鈕 */}
          <button
            type="submit"
            disabled={!title.trim() || title.trim().length < 3 || creating || checkingCount}
            className="w-full py-3 bg-gradient-to-r from-sky-500 to-sky-500
                       text-white rounded-xl font-semibold text-[17px]
                       hover:shadow-lg hover:-translate-y-0.5
                       transition-all duration-200
                       disabled:opacity-50 disabled:cursor-not-allowed
                       disabled:transform-none disabled:shadow-none"
          >
            {checkingCount ? (
              'Checking...'
            ) : creating ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </span>
            ) : (
              'Create Goal'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ObjectiveCreateModal;
